import { Injectable } from '@angular/core';

export interface Palette {
  name: string;
  bg: string;
  outline: string;
  body: string;
  hi: string;
  eye: string;
  mark: string;
}

export interface SpriteData {
  pixels: Uint8Array;
  pal: Palette;
  G: number;
}

export const PX = {
  BG:        0, // transparent
  OUTLINE:   1,
  BODY:      2,
  HI:        3,
  EYE:       4,
  MARK:      5, // opaque face background (dark area around eyes)
  EYE_SHINE: 6, // unused in 4x4 but kept for interface compat
} as const;

// Outline doubles as the text color for player names, so it must clear
// ~3.0:1 contrast against the dark panel surface (#1a1c2e).
export const PALETTES: Palette[] = [
  { name: 'Purple',     bg: '#2a2a4a', outline: '#7b6cdf', body: '#9b8aef', hi: '#b8a8ff', eye: '#ffffff', mark: '#2a2a4a' },
  { name: 'Green',      bg: '#2a3a2a', outline: '#4a9a4a', body: '#68c068', hi: '#90e890', eye: '#ffffff', mark: '#2a3a2a' },
  { name: 'Red',        bg: '#3a2a2a', outline: '#c05050', body: '#e07070', hi: '#ff9898', eye: '#ffffff', mark: '#3a2a2a' },
  { name: 'Blue',       bg: '#2a2a3a', outline: '#5080c8', body: '#70a0e0', hi: '#98c0f8', eye: '#ffffff', mark: '#2a2a3a' },
  { name: 'Gold',       bg: '#3a3a28', outline: '#b8a030', body: '#d8c048', hi: '#f0d868', eye: '#ffffff', mark: '#3a3a28' },
  { name: 'Pink',       bg: '#3a2a35', outline: '#b050a0', body: '#d070c0', hi: '#e898d8', eye: '#ffffff', mark: '#3a2a35' },
  { name: 'Teal',       bg: '#2a3838', outline: '#40a0a0', body: '#60c0c0', hi: '#88e0e0', eye: '#ffffff', mark: '#2a3838' },
  { name: 'Orange',     bg: '#352a20', outline: '#c07838', body: '#d89858', hi: '#f0b878', eye: '#ffffff', mark: '#352a20' },
  { name: 'Gray',       bg: '#2e2e2e', outline: '#787878', body: '#989898', hi: '#b8b8b8', eye: '#d8d8d8', mark: '#2e2e2e' },
  { name: 'Lavender',   bg: '#302838', outline: '#8868b8', body: '#a088d0', hi: '#b8a8e8', eye: '#ffffff', mark: '#302838' },
  { name: 'Lime',       bg: '#28302a', outline: '#70b050', body: '#90c870', hi: '#b0e090', eye: '#ffffff', mark: '#28302a' },
  { name: 'Salmon',     bg: '#382828', outline: '#b86050', body: '#d08070', hi: '#e8a090', eye: '#ffffff', mark: '#382828' },
  { name: 'Sky',        bg: '#283038', outline: '#5888a8', body: '#78a8c8', hi: '#98c8e0', eye: '#ffffff', mark: '#283038' },
  { name: 'Tan',        bg: '#383028', outline: '#a89048', body: '#c0a860', hi: '#d8c080', eye: '#ffffff', mark: '#383028' },
  { name: 'Orchid',     bg: '#302030', outline: '#9858a0', body: '#b078b8', hi: '#c898d0', eye: '#ffffff', mark: '#302030' },
  { name: 'Mint',       bg: '#203030', outline: '#389088', body: '#58b0a8', hi: '#78d0c8', eye: '#ffffff', mark: '#203030' },
  { name: 'Midnight',   bg: '#0e0a20', outline: '#694bff', body: '#2e2058', hi: '#4a3a7a', eye: '#ffffff', mark: '#0e0a20' },
  { name: 'Ink',        bg: '#080808', outline: '#a05858', body: '#2e2e2e', hi: '#4e4e4e', eye: '#ffffff', mark: '#080808' },
  { name: 'Steel',      bg: '#24282e', outline: '#7890a8', body: '#98b0c8', hi: '#b8c8d8', eye: '#ffffff', mark: '#24282e' },
  { name: 'Pine',       bg: '#081410', outline: '#1b9b4e', body: '#1e4830', hi: '#386848', eye: '#ffffff', mark: '#081410' },
  { name: 'Indigo',     bg: '#222438', outline: '#626fff', body: '#5060c0', hi: '#7888d8', eye: '#ffffff', mark: '#222438' },
  { name: 'Magenta',    bg: '#35202e', outline: '#d84098', body: '#e830a0', hi: '#ff58c0', eye: '#ffffff', mark: '#35202e' },
  { name: 'Espresso',   bg: '#1a0e08', outline: '#927f77', body: '#4a3020', hi: '#6a4a38', eye: '#ffffff', mark: '#1a0e08' },
  { name: 'Chartreuse', bg: '#2e3018', outline: '#a8c038',   body: '#c0d858', hi: '#d8e878', eye: '#ffffff', mark: '#2e3018' },
  { name: 'Rose',       bg: '#38202a', outline: '#d06878',   body: '#e08898', hi: '#f0a8b8', eye: '#ffffff', mark: '#38202a' },
  { name: 'Cyan',       bg: '#183038', outline: '#38b0d8',   body: '#58c8e0', hi: '#80dcf0', eye: '#ffffff', mark: '#183038' },
  { name: 'Amber',      bg: '#382818', outline: '#e0a038',   body: '#f0b858', hi: '#ffd078', eye: '#ffffff', mark: '#382818' },
  { name: 'Emerald',    bg: '#183020', outline: '#38c080',   body: '#58d898', hi: '#80e8b0', eye: '#ffffff', mark: '#183020' },
];

function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function makeRng(addr: string): () => number {
  let s = hashStr(addr);
  return () => {
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    return (s >>> 0) / 0xffffffff;
  };
}

// 0=bg(transparent), 1=outline, 2=body

const BODIES: number[][][] = [
  [[0,1,1,0], [1,2,2,1], [1,2,2,1], [0,1,1,0]], // Round
  [[1,1,1,1], [1,2,2,1], [1,2,2,1], [0,1,1,0]], // Flat top
  [[1,0,0,1], [1,1,1,1], [1,2,2,1], [0,1,1,0]], // Horns
  [[1,1,0,0], [1,2,2,1], [1,2,2,1], [0,1,1,0]], // Left ear
  [[0,0,1,1], [1,2,2,1], [1,2,2,1], [0,1,1,0]], // Right ear
  [[1,0,1,0], [1,1,1,1], [1,2,2,1], [0,1,1,0]], // Crown
  [[0,1,0,0], [1,2,2,1], [1,2,2,1], [0,1,1,0]], // Antenna
  [[1,1,1,1], [1,2,2,1], [1,2,2,1], [1,1,1,1]], // Square
  [[0,1,1,0], [1,2,2,1], [1,2,2,1], [1,0,1,0]], // Ghost
  [[0,1,1,1], [1,2,2,1], [1,2,2,1], [1,1,1,0]], // Asymmetric
  [[0,1,1,0], [1,2,2,1], [1,2,2,1], [1,1,1,1]], // Tall round
  [[0,0,0,1], [0,1,1,1], [1,2,2,1], [0,1,1,0]], // Spike right
  [[0,0,1,0], [0,1,1,1], [1,2,2,1], [0,1,1,0]], // Pyramid
  [[1,1,1,1], [1,2,2,1], [1,2,2,1], [1,0,0,1]], // Slab
];

// EVM addresses are case-insensitive; canonicalize so EIP-55 and lowercase forms match.
const paletteCache = new Map<string, Palette>();
export function getPaletteForAddress(addr: string): Palette {
  const key = addr.toLowerCase();
  let palette = paletteCache.get(key);
  if (!palette) {
    const rng = makeRng(key);
    palette = PALETTES[Math.floor(rng() * PALETTES.length)];
    paletteCache.set(key, palette);
  }
  return palette;
}

// Each logical cell in the 4x4 template becomes a CELL_SCALE × CELL_SCALE
// block in the output, so the final sprite is (4*CELL_SCALE)² pixels.
const LOGICAL = 4;
const CELL_SCALE = 4;
const OUTPUT_G = LOGICAL * CELL_SCALE;

function generateSprite(addr: string): SpriteData {
  const rng = makeRng(addr.toLowerCase());
  const pal = PALETTES[Math.floor(rng() * PALETTES.length)];
  const body = BODIES[Math.floor(rng() * BODIES.length)];

  const grid: number[] = [];
  for (let r = 0; r < LOGICAL; r++) {
    for (let c = 0; c < LOGICAL; c++) {
      grid.push(body[r][c]);
    }
  }

  const hiCount = 1 + Math.floor(rng() * 2);
  for (let h = 0; h < hiCount; h++) {
    const i = Math.floor(rng() * LOGICAL * LOGICAL);
    if (grid[i] === 2) grid[i] = 3;
  }

  let eyeRow = -1;
  for (let r = 0; r < LOGICAL; r++) {
    if (grid[r * LOGICAL + 1] >= 1 && grid[r * LOGICAL + 3] >= 1) {
      grid[r * LOGICAL + 0] = 0;
      grid[r * LOGICAL + 1] = 4;
      grid[r * LOGICAL + 2] = 0;
      grid[r * LOGICAL + 3] = 4;
      eyeRow = r;
      break;
    }
  }

  const pixels = new Uint8Array(OUTPUT_G * OUTPUT_G);
  for (let r = 0; r < LOGICAL; r++) {
    for (let c = 0; c < LOGICAL; c++) {
      const v = grid[r * LOGICAL + c];
      let px: number;
      if (v === 0) {
        px = (r === eyeRow) ? PX.MARK : PX.BG;
      } else if (v === 1) {
        px = PX.OUTLINE;
      } else if (v === 2) {
        px = PX.BODY;
      } else if (v === 3) {
        px = PX.HI;
      } else {
        px = PX.EYE;
      }
      for (let dy = 0; dy < CELL_SCALE; dy++) {
        for (let dx = 0; dx < CELL_SCALE; dx++) {
          pixels[(r * CELL_SCALE + dy) * OUTPUT_G + (c * CELL_SCALE + dx)] = px;
        }
      }
    }
  }

  return { pixels, pal, G: OUTPUT_G };
}

@Injectable({ providedIn: 'root' })
export class AvatarService {
  private readonly cache = new Map<string, SpriteData>();

  getSprite(addr: string): SpriteData {
    const key = addr.toLowerCase();
    let sprite = this.cache.get(key);
    if (!sprite) {
      sprite = generateSprite(key);
      this.cache.set(key, sprite);
    }
    return sprite;
  }

  preload(addrs: string[]): void {
    for (const addr of addrs) this.getSprite(addr);
  }
}
